"use client";

import { MouseEventHandler, ReactNode } from "react";

import styles from "./pill.module.scss";

export interface PillProps {
  label: string;
  /** Padding + type scale. Medium is the default filter-pill size. */
  size?: "small" | "medium" | "large";
  /** Selected state: subtle fill, dark-blue border, bold label. */
  selected?: boolean;
  disabled?: boolean;
  /**
   * Looks and announces as disabled but stays hoverable and focusable, so a tooltip can say why it's
   * unavailable. Clicks do nothing.
   */
  ariaDisabled?: boolean;
  /** Optional leading decoration icon (rendered in a size-matched box). */
  leadingIcon?: ReactNode;
  /** Optional trailing action icon — e.g. a caret for dropdowns or an ✕ to clear a filter. */
  trailingIcon?: ReactNode;
  /** Optional secondary text shown after the label (regular weight, truncated). */
  secondaryText?: string;
  ariaPressed?: boolean;
  /** For a pill that opens a panel, such as a filter dropdown. True also gives it the open border. */
  ariaExpanded?: boolean;
  ariaControls?: string;
  ariaLabel?: string;
  /** Set by a wrapping Tooltip so the pill is described by its copy. */
  "aria-describedby"?: string;
  /** Native tooltip text. */
  title?: string;
  className?: string;
  id?: string;
  type?: "button" | "submit" | "reset";
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** `data-*` attributes land on the button, e.g. stable hooks for session-replay tools. */
  [dataAttribute: `data-${string}`]: string | number | boolean | undefined;
}

function Pill({
  label,
  size = "medium",
  selected = false,
  disabled = false,
  ariaDisabled = false,
  leadingIcon,
  trailingIcon,
  secondaryText,
  ariaPressed,
  ariaExpanded,
  ariaControls,
  ariaLabel,
  "aria-describedby": ariaDescribedBy,
  title,
  className,
  id,
  type = "button",
  onClick,
  ...dataAttributes
}: PillProps) {
  const classNames = [
    styles.Pill,
    styles[size],
    selected ? styles.selected : "",
    ariaExpanded ? styles.expanded : "",
    ariaDisabled ? styles.ariaDisabled : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      {...dataAttributes}
      type={type}
      id={id}
      className={classNames}
      onClick={ariaDisabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={ariaDisabled || undefined}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      title={title}
    >
      {leadingIcon ? <span className={styles.icon}>{leadingIcon}</span> : null}
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        {secondaryText ? <span className={styles.secondaryText}>{secondaryText}</span> : null}
      </span>
      {trailingIcon ? <span className={styles.icon}>{trailingIcon}</span> : null}
    </button>
  );
}

export default Pill;
